import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { firstValueFrom } from "rxjs";
import {
  getSalesforceWebTransport,
  SALESFORCE_WEB_DIRECT_CONFIG,
} from "@app/config/salesforce.config";
import { SalesforceWebAttributes } from "./salesforce-web.types";

const SF_LOG_STYLE = "color: #FF8C00; font-weight: bold;";
const SF_LOG_WARN_STYLE = "color: #FFA500; font-weight: bold;";

interface McTokenResponse {
  access_token: string;
  rest_instance_url?: string;
  expires_in?: number;
}

interface McEventResponse {
  eventInstanceId?: string;
}

/**
 * SF-WEB-PROVISIONAL — arquivo inteiro só para teste local.
 *
 * - Ativo quando getSalesforceWebTransport() === "direct"
 * - Requer ionic serve + proxy.conf.json
 * - Produção: usar transport "backend" (forçado em environment production)
 *
 * Não estender para produção. Mapa: docs/SALESFORCE_INTEGRATION.md
 */
@Injectable({
  providedIn: "root",
})
export class SalesforceWebDirectService {
  private cachedToken: string | null = null;
  private tokenExpiresAt = 0;
  private restBaseUrl: string | null = null;
  private configWarningLogged = false;
  private proxyWarningLogged = false;
  private directSendPausedLogged = false;

  constructor(private http: HttpClient) {}

  async trackEvent(
    eventName: string,
    contactKey: string | null,
    attributes: SalesforceWebAttributes
  ): Promise<boolean> {
    if (!SALESFORCE_WEB_DIRECT_CONFIG.enabled || getSalesforceWebTransport() !== "direct") {
      return false;
    }

    if (!this.hasRestOAuthCredentials()) {
      this.logDirectSendPausedOnce(eventName);
      return false;
    }

    try {
      const token = await this.getAccessToken();
      const restBase = this.resolveRestBaseUrl(token.restInstanceUrl);
      const eventDefinitionKey = this.resolveEventDefinitionKey(eventName);
      const key = contactKey || "WEB_ANONYMOUS";

      const body = {
        ContactKey: key,
        EventDefinitionKey: eventDefinitionKey,
        Data: attributes,
      };

      const url = `${restBase}/interaction/v1/events`;
      const headers = new HttpHeaders({
        Authorization: `Bearer ${token.accessToken}`,
        "Content-Type": "application/json",
      });

      const response = await firstValueFrom(
        this.http.post<McEventResponse>(url, body, { headers })
      );

      console.log(
        `%c[Salesforce Web Direct] Evento SFMC: ${eventName}`,
        SF_LOG_STYLE,
        { eventDefinitionKey, contactKey: key, eventInstanceId: response?.eventInstanceId, attributes }
      );
      return true;
    } catch (err) {
      console.warn(`%c[Salesforce Web Direct] Falha: ${eventName}`, SF_LOG_WARN_STYLE, err);
      this.logProxyHintIfNeeded(err);
      this.logConfigHintOnce();
      return false;
    }
  }

  /** Contact sync direto não implementado — use backend em produção. */
  async syncContact(): Promise<boolean> {
    return true;
  }

  private async getAccessToken(): Promise<{ accessToken: string; restInstanceUrl?: string }> {
    if (this.cachedToken && Date.now() < this.tokenExpiresAt) {
      return { accessToken: this.cachedToken, restInstanceUrl: this.restBaseUrl ?? undefined };
    }

    const { clientId, clientSecret } = this.resolveOAuthCredentials();
    const authUrl = SALESFORCE_WEB_DIRECT_CONFIG.useDevProxy
      ? "/sfmc-auth/v2/token"
      : `https://${SALESFORCE_WEB_DIRECT_CONFIG.subdomain}.auth.marketingcloudapis.com/v2/token`;

    const body = {
      grant_type: "client_credentials",
      client_id: clientId,
      client_secret: clientSecret,
    };

    const response = await firstValueFrom(
      this.http.post<McTokenResponse>(authUrl, body, {
        headers: new HttpHeaders({ "Content-Type": "application/json" }),
      })
    );

    this.cachedToken = response.access_token;
    const ttlMs = (response.expires_in ?? 1200) * 1000;
    this.tokenExpiresAt = Date.now() + ttlMs - 60_000;
    if (response.rest_instance_url) {
      this.restBaseUrl = response.rest_instance_url.replace(/\/$/, "");
    }

    return { accessToken: this.cachedToken, restInstanceUrl: response.rest_instance_url };
  }

  private resolveRestBaseUrl(restInstanceFromToken?: string): string {
    if (restInstanceFromToken) {
      return restInstanceFromToken.replace(/\/$/, "");
    }
    if (this.restBaseUrl) {
      return this.restBaseUrl;
    }
    if (SALESFORCE_WEB_DIRECT_CONFIG.useDevProxy) {
      return "/sfmc-rest";
    }
    return `https://${SALESFORCE_WEB_DIRECT_CONFIG.subdomain}.rest.marketingcloudapis.com`;
  }

  private hasRestOAuthCredentials(): boolean {
    return Boolean(
      SALESFORCE_WEB_DIRECT_CONFIG.restClientId?.trim() &&
        SALESFORCE_WEB_DIRECT_CONFIG.restClientSecret?.trim()
    );
  }

  private resolveOAuthCredentials(): { clientId: string; clientSecret: string } {
    const clientId = SALESFORCE_WEB_DIRECT_CONFIG.restClientId.trim();
    const clientSecret = SALESFORCE_WEB_DIRECT_CONFIG.restClientSecret.trim();
    return { clientId, clientSecret };
  }

  private logDirectSendPausedOnce(eventName: string): void {
    if (this.directSendPausedLogged) {
      return;
    }
    this.directSendPausedLogged = true;
    console.warn(
      `%c[Salesforce Web Direct] Envio ao SFMC pausado (${eventName} e demais). ` +
        "O log [Salesforce Events] >> continua no console. " +
        "Para enviar de verdade: preencha restClientId/restClientSecret (API Integration REST) em salesforce.config.ts. " +
        "appId/accessToken do Mobile Push não funcionam no OAuth REST (evita 401).",
      SF_LOG_WARN_STYLE
    );
  }

  private resolveEventDefinitionKey(eventName: string): string {
    return (
      SALESFORCE_WEB_DIRECT_CONFIG.eventDefinitionKeys[eventName] ?? eventName
    );
  }

  private logProxyHintIfNeeded(err: unknown): void {
    if (this.proxyWarningLogged || !SALESFORCE_WEB_DIRECT_CONFIG.useDevProxy) {
      return;
    }
    const httpErr = err as { status?: number; url?: string };
    const isProxy404 =
      httpErr.status === 404 &&
      typeof httpErr.url === "string" &&
      (httpErr.url.includes("/sfmc-auth") || httpErr.url.includes("/sfmc-rest"));
    if (!isProxy404) {
      return;
    }
    this.proxyWarningLogged = true;
    console.warn(
      "%c[Salesforce Web Direct] Proxy do dev server inativo (404 em /sfmc-auth). " +
        "Pare o servidor, suba de novo com proxy: `npm start` ou `npm run start:ionic`. " +
        "Confira proxy.conf.js em angular.json → serve.options.proxyConfig.",
      SF_LOG_WARN_STYLE
    );
  }

  private logConfigHintOnce(): void {
    if (this.configWarningLogged) {
      return;
    }
    this.configWarningLogged = true;
    console.warn(
      "%c[Salesforce Web Direct] Preencha restClientId/restClientSecret em salesforce.config.ts " +
        "(pacote Installed → API Integration no Marketing Cloud).",
      SF_LOG_WARN_STYLE
    );
  }
}
